import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import type { EChartsInstance } from "echarts-for-react";
import * as echarts from "echarts";
import type { WeekDetails, WeeklyTrend } from "../../../types/weekly-cost-report.types";
import { ConversionToK } from "../../../utils/shared/costCalculations.utils";
import { buildTooltipForCharts } from "../../../utils/shared/costTooltip.utils";
import {
  formatDate,
  getPreviousWeekDate,
} from "../../../utils/weekly-cost-report/weeklyCostTimeRange.utils";
import theme, { ACCENT, CHART } from "@/config/theme";
import { useTheme } from "@/contexts/ThemeContext";

interface WeeklyCostTrendChartProps {
  trend?: WeeklyTrend[] | null;
  currentWeek: WeekDetails;
  previousWeek: WeekDetails;
  avgDailyCost?: number;
  onReady?: (instance: EChartsInstance) => void;
}

const WeeklyCostTrendChart = ({
  trend,
  currentWeek,
  previousWeek,
  avgDailyCost,
  onReady,
}: WeeklyCostTrendChartProps) => {
  const { theme: themeName } = useTheme();
  const normalizedTrendData = useMemo(
    () => (Array.isArray(trend) ? trend : []),
    [trend],
  );
  const hasData = normalizedTrendData.length > 0;

  const isSpike = (cost: number) =>
    cost > (avgDailyCost ? avgDailyCost * 1.2 : 0);

  const days = useMemo(
    () => normalizedTrendData.map((d) => d.dayName.slice(0, 3)),
    [normalizedTrendData],
  );

  // Current week series with static colors
  const dotColor = themeName === "dark" ? ACCENT.aqua : CHART.linePrimary;
  const currentSeries = useMemo(
    () =>
      normalizedTrendData.map((d) => ({
        value: d.currentCost ?? 0,
        itemStyle: {
          color: isSpike(d.currentCost) ? theme.status.error : dotColor,
        },
      })),
    [normalizedTrendData, avgDailyCost, themeName],
  );

  // Previous week series
  const previousSeries = useMemo(
    () => normalizedTrendData.map((d) => d.previousCost ?? 0),
    [normalizedTrendData],
  );

  // Spike markers
  const markPoints = useMemo(
    () =>
      normalizedTrendData.flatMap((d, index) =>
        isSpike(d.currentCost)
          ? [
              {
                coord: [index, d.currentCost],
                itemStyle: {
                  color: theme.status.error,
                  borderColor: theme.text.primary,
                  borderWidth: 2,
                },
              },
            ]
          : [],
      ),
    [normalizedTrendData, avgDailyCost, themeName],
  );


  const option = useMemo(
    () => ({
      animation: false,

      tooltip: {
        trigger: "axis",
        backgroundColor: CHART.tooltipBg,
        borderColor: ACCENT.aqua,
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          color: CHART.tooltipText,
        },
        extraCssText: `box-shadow: ${CHART.tooltipShadow};`,
        formatter: (params: echarts.TooltipComponentFormatterCallbackParams) => {
          if (!params) return "";
          const points = Array.isArray(params) ? params : [params];
          if (points.length === 0) return "";

          const currentPoint = points[0];
          if (!currentPoint) return "";
          if (typeof currentPoint.dataIndex !== "number") return "";

          const d = normalizedTrendData[currentPoint.dataIndex];
          if (!d) return "";

          return buildTooltipForCharts({
            type: "trend",
            data: d,
            currentColor:
              typeof currentPoint.color === "string" ? currentPoint.color : "",
            previousColor:
              typeof points[1]?.color === "string" ? points[1].color : "",
            getPreviousWeekDate,
            formatDate,
            isSpike,
            avgDailyCost: avgDailyCost,
          });
        },
      },

      legend: {
        top: 6,
        right: 10,
        itemWidth: 8,
        itemHeight: 8,
        icon: "circle",
        textStyle: {
          fontSize: 11,
          color: theme.text.primary,
          fontWeight: 500,
        },
         inactiveColor: theme.text.disabled,
        data: [
          {
            name: currentWeek.label,
            icon: "circle",
            itemStyle: {
              color: dotColor,
            },
          },
          {
            name: previousWeek.label,
            icon: "circle",
            itemStyle: {
              color: theme.text.tertiary,
            },
          },
        ],
      },

      grid: {
        top: 40,
        right: 34,
        bottom: 70,
        left: 36,
        containLabel: true,
      },

      xAxis: {
        type: "category",
        data: days,
        boundaryGap: true,
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisLabel: {
          show: true,
          interval: 0,
          hideOverlap: false,
          showMinLabel: true,
          showMaxLabel: true,
          color: theme.text.tertiary,
          fontSize: 10,
          margin: 12,
        },
      },

      yAxis: {
        type: "value",
        splitLine: {
          lineStyle: {
            color: theme.border.subtle,
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: theme.text.tertiary,
          fontSize: 10,
          formatter: (v: number) => `$${ConversionToK(v)}`,
        },
      },
      series: [
        {
          name: currentWeek.label,
          type: "line",
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          lineStyle: {
            color: dotColor,
            width: 2.5,
          },
          itemStyle: {
            color: dotColor,
          },
          // Use memoized per-point series so spikes keep their itemStyle
          data: currentSeries,
          markPoint: {
            symbol: "circle",
            symbolSize: 8,
            data: markPoints,
          },
          showSymbol: true,
        },
        {
          name: previousWeek.label,
          type: "line",
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          lineStyle: {
            color: theme.text.tertiary,
            width: 2,
          },
          itemStyle: {
            color: theme.text.tertiary,
          },
          // Use memoized previous series array
          data: previousSeries,
          areaStyle: {
            color: CHART.weeklyAreaGradientStart,
          },
        },
      ],
  }),
    [
      normalizedTrendData,
      days,
      currentSeries,
      previousSeries,
      markPoints,
      currentWeek,
      previousWeek,
      avgDailyCost,
      themeName,
    ],
  );

  if (!hasData) {
    return (
      <div className="w-full h-full flex items-center justify-center text-text-tertiary">
        No trend data available
      </div>
    );
  }

  return (
    <ReactECharts
      key={themeName}
      option={option}
      className="h-full w-full"
      notMerge
      lazyUpdate
      opts={{ renderer: "canvas" }}
      onChartReady={onReady}
    />
  );
};

export default WeeklyCostTrendChart;
